// Painted lighting: a fixed warm key light, a cool hemisphere fill, the TSL port of oneshot-rts
// shading.ts (3-band N·L ramp, warm key / cool fill tints, rim), plus background and aerial haze.
//
//     PBR (three) ──► directDiffuse, indirectDiffuse, specular
//                          │
//        ramp(N·L) ────────┼──► directDiffuse   = albedo · key · ramp · rampLight
//        hemisphere ───────┼──► indirectDiffuse · rampDark (cool shadow tint)
//        1 − N·V ──────────┴──► rim, added to specular
//
// The key light never moves (a readable map needs uniform light); it follows the camera target
// only so its shadow map covers the view.

import * as THREE from 'three/webgpu';
import {
  BRDF_GGX, BRDF_Lambert, Fn, color, diffuseColor, float, fog, mix, normalView, normalWorldGeometry, positionViewDirection,
  positionWorld, pow, rangeFogFactor, roughness, screenUV, select, smoothstep, uniform, vec2, vec3, vec4,
} from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import type UniformNode from 'three/src/nodes/core/UniformNode.js';
import type NodeBuilder from 'three/src/nodes/core/NodeBuilder.js';
import type { LightingModelDirectInput } from 'three/src/nodes/core/LightingModel.js';
import { hash12, worldNoise } from './tsl/noise.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** The light rig's look, all in one place. Colours are sRGB hex. */
export const LIGHT_LOOK = {
  // Warm, but only slightly: a warm sun times a warm ramp crushes blue and turns greens lime.
  sunColor: '#fff7ea',
  sunIntensity: 3.5,
  /** Direction toward the sun: from the west-south-west, ~46° up (camera looks north). */
  sunDir: [-0.62, 0.72, 0.3] as const,
  skyColor: '#b3cff0',
  groundColor: '#7a6a52',
  fillIntensity: 1.25,
  rampDark: '#bec3d6',
  rampLight: '#fffaf0',
  rim: '#ffe9c8',
  rimStrength: 0.2,
  /** Distance haze: a muted ocean hue, so the far map melts into sea air, not grey. */
  haze: '#7fa8c4',
  /** Sky at the horizon (seen only at glancing tilts); rises to skyZenith. */
  skyHorizon: '#e9eef2',
  /** Frame around the map at world zoom (view rays well below the horizon). */
  backgroundTop: '#0d1c2e',
  backgroundBottom: '#1a3552',
  /** Space behind the globe: deep blue-black, a little lighter toward the bottom. */
  spaceTop: '#02050b',
  spaceBottom: '#0a1626',
  star: '#dfe9ff',
  /** Sky above the horizon, seen only at glancing tilts. */
  skyZenith: '#9fc3de',
  exposure: 1.0,
} as const;

// Ramp bands (after shading.ts paintRamp): shadow, half-tone, light. Flat ground (N·L ≈ 0.72,
// 0.78 after the wrap) sits halfway up the light band, so slopes facing the sun still brighten and
// slopes turned away step down through the half-tone: relief reads from both sides.
const BAND_A = [0.0, 0.14, 0.4] as const;
const BAND_B = [0.22, 0.44, 0.34] as const;
const BAND_C = [0.62, 0.95, 0.26] as const;
const RAMP_WOBBLE = 0.12; // brush wobble of the terminator
const RAMP_WRAP = 0.3; // faces up to this far past the terminator still catch the first band
const RAMP_WOBBLE_FREQ = 1.3;
const SPECULAR_SCALE = 0.35;
const RIM_POWER = 3;
const SHADOW_SPAN_MIN = 30; // world units the shadow map covers at close zoom
const SHADOW_SPAN_PER_HEIGHT = 2.2;
const SHADOW_MAX_HEIGHT = 160; // above this camera height shadows are off
const SHADOW_FADE_FROM = 0.75; // shadows start fading at this share of SHADOW_MAX_HEIGHT
const SUN_DISTANCE = 80;

/** Painted-look uniforms. Nodes for shader maths; mutate `colors` / `rimStrength.value` to restyle. */
export interface PaintStyle {
  rampDark: V3;
  rampLight: V3;
  rim: V3;
  rimStrength: UniformNode<'float', number>;
  /** Scale of the key light's specular highlight. */
  specular: UniformNode<'float', number>;
  colors: { rampDark: THREE.Color; rampLight: THREE.Color; rim: THREE.Color };
}

/** A colour uniform typed as vec3 for TSL maths (the typings keep 'color' and 'vec3' apart). */
export function colorUniform(c: THREE.Color): V3 {
  return uniform(c) as unknown as V3;
}

export function createPaintStyle(): PaintStyle {
  const colors = {
    rampDark: new THREE.Color(LIGHT_LOOK.rampDark),
    rampLight: new THREE.Color(LIGHT_LOOK.rampLight),
    rim: new THREE.Color(LIGHT_LOOK.rim),
  };
  return {
    rampDark: colorUniform(colors.rampDark),
    rampLight: colorUniform(colors.rampLight),
    rim: colorUniform(colors.rim),
    rimStrength: uniform(LIGHT_LOOK.rimStrength),
    specular: uniform(SPECULAR_SCALE),
    colors,
  };
}

/** Three soft bands: shadow, half-tone, light. Returns 0..1. */
export function paintRamp(nl: F): F {
  const a = smoothstep(BAND_A[0], BAND_A[1], nl).mul(BAND_A[2]);
  const b = smoothstep(BAND_B[0], BAND_B[1], nl).mul(BAND_B[2]);
  const c = smoothstep(BAND_C[0], BAND_C[1], nl).mul(BAND_C[2]);
  return a.add(b).add(c);
}

interface Reflected {
  directDiffuse: V3;
  directSpecular: V3;
  indirectDiffuse: V3;
  indirectSpecular: V3;
}

/** Physical lighting with the key light's Lambert term replaced by the painted ramp. */
export class PaintedLightingModel extends THREE.PhysicalLightingModel {
  private readonly style: PaintStyle;

  constructor(style: PaintStyle) {
    super();
    this.style = style;
  }

  override direct(input: LightingModelDirectInput): void {
    const lightDirection = input.lightDirection as V3;
    const lightColor = input.lightColor as V3;
    const reflectedLight = input.reflectedLight as unknown as Reflected;
    const s = this.style;
    const nl = normalView.dot(lightDirection).add(RAMP_WRAP).div(1 + RAMP_WRAP).clamp();
    const wobble = worldNoise(positionWorld.xz, RAMP_WOBBLE_FREQ).sub(0.5).mul(RAMP_WOBBLE);
    const ramp = paintRamp(nl.add(wobble));
    const irradiance = lightColor.mul(ramp).mul(s.rampLight);

    const lambert = BRDF_Lambert({ diffuseColor: diffuseColor.rgb }) as unknown as V3;
    reflectedLight.directDiffuse.addAssign(irradiance.mul(lambert));
    // GGX carries 1 / (N·L) in its visibility term, which only the physical N·L factor cancels: weigh
    // the highlight by true N·L (not the wrapped ramp), or grazing facets flash white.
    const spec = BRDF_GGX({ lightDirection, f0: vec3(0.04), f90: float(1), roughness }) as unknown as V3;
    const dotNL = normalView.dot(lightDirection).clamp();
    reflectedLight.directSpecular.addAssign(lightColor.mul(dotNL).mul(spec).mul(s.specular));

    const nv = normalView.dot(positionViewDirection).clamp();
    const rim = pow(nv.oneMinus(), RIM_POWER).mul(ramp.mul(0.65).add(0.35));
    reflectedLight.directSpecular.addAssign(s.rim.mul(s.rimStrength).mul(rim).mul(diffuseColor.rgb.mul(0.6).add(0.4)));
  }

  override indirectDiffuse(builder: NodeBuilder): void {
    super.indirectDiffuse(builder);
    const reflected = (builder.context as unknown as { reflectedLight: Reflected }).reflectedLight;
    reflected.indirectDiffuse.mulAssign(this.style.rampDark);
  }
}

/** MeshStandardNodeMaterial lit by the painted model. Terrain and every prop share this look. */
export class PaintedMaterial extends THREE.MeshStandardNodeMaterial {
  private readonly style: PaintStyle;

  constructor(style: PaintStyle, params?: THREE.MeshStandardNodeMaterialParameters) {
    super(params);
    this.style = style;
  }

  override setupLightingModel(): THREE.PhysicalLightingModel {
    return new PaintedLightingModel(this.style);
  }
}

export interface LightRig {
  sun: THREE.DirectionalLight;
  fill: THREE.HemisphereLight;
  style: PaintStyle;
  /** Keep the shadow map over the view. */
  follow(targetX: number, targetZ: number, camHeight: number): void;
  setShadows(on: 'on' | 'off', mapSize: number): void;
}

export function createLights(scene: THREE.Scene): LightRig {
  const style = createPaintStyle();
  const sun = new THREE.DirectionalLight(new THREE.Color(LIGHT_LOOK.sunColor), LIGHT_LOOK.sunIntensity);
  const dir = new THREE.Vector3(...LIGHT_LOOK.sunDir).normalize();
  sun.shadow.bias = -0.0008;
  sun.shadow.normalBias = 0.06;
  scene.add(sun, sun.target);

  const fill = new THREE.HemisphereLight(new THREE.Color(LIGHT_LOOK.skyColor), new THREE.Color(LIGHT_LOOK.groundColor), LIGHT_LOOK.fillIntensity);
  scene.add(fill);

  let shadowsWanted = false;
  return {
    sun,
    fill,
    style,
    follow(x, z, h) {
      sun.target.position.set(x, 0, z);
      sun.position.set(x + dir.x * SUN_DISTANCE, dir.y * SUN_DISTANCE, z + dir.z * SUN_DISTANCE);
      sun.target.updateMatrixWorld();
      // Fade shadows out with height instead of toggling castShadow: in the WebGPU renderer,
      // castShadow off disposes the shadow map, and turning it back on then crashes the shadow
      // pass (null depthTexture) and invalidates the scene pipeline (harness: zoom in, out, in).
      const strength = shadowsWanted ? 1 - smoothstep01(SHADOW_MAX_HEIGHT * SHADOW_FADE_FROM, SHADOW_MAX_HEIGHT, h) : 0;
      sun.shadow.intensity = strength;
      sun.shadow.autoUpdate = strength > 0;
      const span = Math.max(SHADOW_SPAN_MIN, h * SHADOW_SPAN_PER_HEIGHT);
      const cam = sun.shadow.camera;
      if (cam.right !== span) {
        cam.left = -span;
        cam.right = span;
        cam.top = span;
        cam.bottom = -span;
        cam.near = 1;
        cam.far = SUN_DISTANCE * 2;
        cam.updateProjectionMatrix();
      }
    },
    setShadows(on, mapSize) {
      shadowsWanted = on === 'on';
      sun.shadow.mapSize.set(mapSize, mapSize);

      // Once on, castShadow stays on for the same reason; 'off' is intensity 0 without updates.
      sun.castShadow = sun.castShadow || shadowsWanted;
    },
  };
}

export interface Atmosphere {
  /** Update haze distances from the haze-scaled camera height and the background from its pitch (degrees). */
  update(camHeight: number, pitch: number): void;
  /** Fade the frame around the map into starry space (the globe view): 0 frame, 1 space. */
  setSpace(amount: number): void;
}

const HAZE_NEAR_PER_HEIGHT = 1.6;
const HAZE_FAR_PER_HEIGHT = 7;
const HAZE_MAX = 0.35;
// Past HAZE_FAR the haze thickens to opaque, so the far ground melts into the horizon and the map
// edge never shows (camera.ts HAZE_REACH must equal HAZE_FULL_PER_HEIGHT).
const HAZE_FULL_PER_HEIGHT = 11;

// Background bands by view-ray elevation (dir.y): the frame below, haze at the horizon, sky above.
const HORIZON_BELOW = 0.3;
const HORIZON_ABOVE = 0.5;
// Just below the horizon the haze brightens toward the sky colour (the painted horizon glow).
const HORIZON_GLOW = 0.1;
const HORIZON_GLOW_MIX = 0.85;
// The dark frame around the map only reads from a steep view; a tilted one sees haze instead.
const FRAME_PITCH = [55, 75] as const;

// Stars: a grid of cells over the view direction, a share of them lit.
const STAR_CELLS = 300;
const STAR_SHARE = 0.02;
const STAR_SIZE = 0.18;

/**
 * Background and aerial haze. The background is chosen by view direction, so one node serves both
 * extremes: straight down at world zoom it is a dark vignetted frame around the map; at a glancing
 * tilt the far map edge melts into horizon haze (the fog colour) under a blue sky.
 */
export function createAtmosphere(scene: THREE.Scene): Atmosphere {
  const near = uniform(20);
  const far = uniform(120);
  const full = uniform(220);
  const frameWeight = uniform(1);
  const top = color(LIGHT_LOOK.backgroundTop);
  const bottom = color(LIGHT_LOOK.backgroundBottom);
  const haze = color(LIGHT_LOOK.haze);
  const zenith = color(LIGHT_LOOK.skyZenith);
  const horizon = color(LIGHT_LOOK.skyHorizon);

  const space = uniform(0);
  const spaceTop = color(LIGHT_LOOK.spaceTop);
  const spaceBottom = color(LIGHT_LOOK.spaceBottom);
  const starColor = color(LIGHT_LOOK.star);

  const background = Fn(() => {
    // Inside the background sphere the geometry normal is the view direction.
    const dirY = normalWorldGeometry.y;
    const uv = screenUV;
    const grad = mix(top, bottom, uv.y.oneMinus());
    const vignette = smoothstep(0.85, 0.2, uv.sub(0.5).length()).mul(0.5).add(0.5);

    // Stars: one jittered point per lit cell, twinkling by brightness only.
    const cell = normalWorldGeometry.xz.mul(STAR_CELLS);
    const id = cell.floor();
    const lit = hash12(id).sub(1 - STAR_SHARE).div(STAR_SHARE).clamp(0, 1).ceil();
    const jitter = vec2(hash12(id.add(17.3)), hash12(id.add(41.9))).sub(0.5).mul(0.5);
    const star = smoothstep(STAR_SIZE, 0, cell.fract().sub(0.5).sub(jitter).length()).mul(lit).mul(hash12(id.add(5.1)).mul(0.7).add(0.3));
    const sky = mix(spaceTop, spaceBottom, uv.y.oneMinus()).mul(vignette).add(starColor.mul(star));
    const frame = mix(grad.mul(vignette), sky, space);
    const glow = mix(haze, horizon, smoothstep(-HORIZON_GLOW, 0, dirY).mul(HORIZON_GLOW_MIX));
    const below = mix(glow, frame, smoothstep(0.0, HORIZON_BELOW, dirY.negate()).mul(frameWeight));
    const above = mix(mix(haze, horizon, smoothstep(0.0, 0.02, dirY)), zenith, smoothstep(0.02, HORIZON_ABOVE, dirY));
    return vec4(select(dirY.lessThan(0), below, above), 1);
  })();

  const base = rangeFogFactor(near, far).mul(HAZE_MAX);
  const factor = base.add(rangeFogFactor(far, full).mul(base.oneMinus()));
  const s = scene as unknown as { backgroundNode: Node | null; fogNode: Node | null };
  s.backgroundNode = background;
  s.fogNode = fog(color(LIGHT_LOOK.haze), factor);

  return {
    update(h, pitch) {
      near.value = h * HAZE_NEAR_PER_HEIGHT;
      far.value = h * HAZE_FAR_PER_HEIGHT;
      full.value = h * HAZE_FULL_PER_HEIGHT;
      frameWeight.value = smoothstep01(FRAME_PITCH[0], FRAME_PITCH[1], pitch);
    },
    setSpace(amount) {
      space.value = amount;
    },
  };
}

/** Hermite step from 0 at e0 to 1 at e1. */
function smoothstep01(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
